import { cn } from "@repo/design-system/utils";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { memo } from "react";
import { navigationItems, type NavigationItem } from "./account-navigation";

/**
 * Mobile navigation component using horizontal tabs
 *
 * Features:
 * - Horizontal scrollable tabs for mobile screens
 * - Keyboard accessible
 * - Screen reader friendly with ARIA labels
 * - Visual active state indication
 * - Memoized for performance
 */
export const AccountMobileNavigation = memo(function AccountMobileNavigation() {
  const router = useRouterState();
  const navigate = useNavigate();
  const currentPath = router.location.pathname;

  /**
   * Determine if a navigation item is active
   */
  const isItemActive = (item: NavigationItem): boolean => {
    if (item.path === "/account") {
      return currentPath === "/account";
    }
    return currentPath.startsWith(item.path);
  };

  /**
   * Handle navigation when tab is clicked
   */
  const handleTabClick = (path: string) => {
    navigate({ to: path });
  };

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (
    event: React.KeyboardEvent,
    index: number,
    path: string
  ) => {
    const tabs = navigationItems;

    switch (event.key) {
      case "ArrowLeft": {
        event.preventDefault();
        const prevIndex = index === 0 ? tabs.length - 1 : index - 1;
        const prevTab = document.querySelector(
          `[data-tab-index="${prevIndex}"]`
        ) as HTMLButtonElement;
        prevTab?.focus();
        break;
      }
      case "ArrowRight": {
        event.preventDefault();
        const nextIndex = index === tabs.length - 1 ? 0 : index + 1;
        const nextTab = document.querySelector(
          `[data-tab-index="${nextIndex}"]`
        ) as HTMLButtonElement;
        nextTab?.focus();
        break;
      }
      case "Enter":
      case " ": {
        event.preventDefault();
        handleTabClick(path);
        break;
      }
      case "Home": {
        event.preventDefault();
        const firstTab = document.querySelector(
          `[data-tab-index="0"]`
        ) as HTMLButtonElement;
        firstTab?.focus();
        break;
      }
      case "End": {
        event.preventDefault();
        const lastTab = document.querySelector(
          `[data-tab-index="${tabs.length - 1}"]`
        ) as HTMLButtonElement;
        lastTab?.focus();
        break;
      }
    }
  };

  return (
    <nav aria-label="Account sections mobile">
      <div
        className="bg-muted scrollbar-hide flex gap-1 overflow-x-auto rounded-lg p-1"
        role="tablist"
        aria-label="Account navigation tabs"
      >
        {navigationItems.map((item, index) => {
          const Icon = item.icon;
          const isActive = isItemActive(item);

          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              data-tab-index={index}
              aria-selected={isActive}
              aria-controls={`panel-${item.id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => handleTabClick(item.path)}
              onKeyDown={(e) => handleKeyDown(e, index, item.path)}
              className={cn(
                "flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-all",
                "focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2",
                isActive && [
                  "bg-card text-purple-600 shadow-sm",
                  "dark:bg-neutral-800 dark:text-purple-400"
                ],
                !isActive && [
                  "text-muted-foreground",
                  "hover:bg-card/50 hover:text-foreground"
                ]
              )}
            >
              <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
});
